import { useEffect } from "react";
import { useWebSocket } from "@/platform/websocket";
import { useToast } from "@bitsentry-ce/components/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "@bitsentry-ce/i18n";
import { VulnerabilitySeverity } from "@bitsentry/core/features/vulnerabilities/application/ports/types";
import { z } from "zod";

const vulnerabilityCreatedPayloadSchema = z.object({
  id: z.string(),
  title: z.string(),
  severity: z.enum(VulnerabilitySeverity),
  type: z.string(),
  cvss: z.number().optional(),
  cve: z.string().optional(),
  component: z.string().optional(),
  description: z.string().optional(),
  createdAt: z.coerce.date(),
});

type VulnerabilityCreatedPayload = z.infer<
  typeof vulnerabilityCreatedPayloadSchema
>;

const getSeverityColor = (
  severity: VulnerabilityCreatedPayload["severity"],
) => {
  switch (severity) {
    case VulnerabilitySeverity.CRITICAL:
      return "text-red-600 dark:text-red-400";
    case VulnerabilitySeverity.HIGH:
      return "text-orange-600 dark:text-orange-400";
    case VulnerabilitySeverity.MEDIUM:
      return "text-yellow-600 dark:text-yellow-400";
    case VulnerabilitySeverity.LOW:
      return "text-blue-600 dark:text-blue-400";
    case VulnerabilitySeverity.INFO:
      return "text-gray-600 dark:text-gray-400";
  }
};

const getSeverityEmoji = (
  severity: VulnerabilityCreatedPayload["severity"],
) => {
  switch (severity) {
    case VulnerabilitySeverity.CRITICAL:
      return "🔥";
    case VulnerabilitySeverity.HIGH:
      return "🚧";
    default:
      return "🚨";
  }
};

function getTitleClassName(isDestructive: boolean): string {
  if (isDestructive) {
    return "font-medium text-white";
  }

  return "font-medium";
}

function getSeverityClassName(
  severity: VulnerabilityCreatedPayload["severity"],
  isDestructive: boolean,
): string {
  if (isDestructive) {
    return "text-sm text-white font-semibold";
  }

  return `text-sm ${getSeverityColor(severity)}`;
}

function getComponentClassName(isDestructive: boolean): string {
  if (isDestructive) {
    return "text-sm text-white/90";
  }

  return "text-sm text-muted-foreground";
}

function getToastVariant(isDestructive: boolean): "destructive" | "default" {
  if (isDestructive) {
    return "destructive";
  }

  return "default";
}

function getToastDuration(isDestructive: boolean): number {
  if (isDestructive) {
    return 30_000;
  }

  return 18_000;
}

export function useVulnerabilityNotifications() {
  const { t } = useTranslation();
  const { on, off, isConnected } = useWebSocket();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    console.log("🔌 WebSocket connected:", isConnected);
    console.log("📡 Setting up vulnerability:created listener");

    const handleVulnerabilityCreated = (data: unknown) => {
      console.log("🚨 Vulnerability created event received:", data);
      const parsedPayload = vulnerabilityCreatedPayloadSchema.safeParse(data);
      if (!parsedPayload.success) {
        console.warn(
          "Ignored malformed vulnerability payload",
          parsedPayload.error,
        );
        return;
      }

      const payload = parsedPayload.data;

      // Invalidate vulnerabilities query to refresh the list
      void queryClient.invalidateQueries({ queryKey: ["vulnerabilities"] });

      // Show toast notification
      console.log("🔔 Showing toast notification for:", payload.title);
      const isDestructive = payload.severity === VulnerabilitySeverity.CRITICAL;
      const titleClassName = getTitleClassName(isDestructive);
      const severityClassName = getSeverityClassName(
        payload.severity,
        isDestructive,
      );
      const componentClassName = getComponentClassName(isDestructive);
      let cvssScore = null;
      if (payload.cvss !== undefined) {
        cvssScore = t("common.useVulnerabilityNotifications.cvssScore", {
          score: payload.cvss,
        });
      }
      let componentLine = null;
      if (payload.component !== undefined && payload.component !== "") {
        componentLine = (
          <p className={componentClassName}>
            {t("common.useVulnerabilityNotifications.component")}{" "}
            {payload.component}
          </p>
        );
      }

      toast({
        title: t(
          "common.useVulnerabilityNotifications.newVulnerabilityDetected",
          {
            icon: getSeverityEmoji(payload.severity),
          },
        ),
        description: (
          <div className="space-y-1">
            <p className={titleClassName}>{payload.title}</p>
            <p className={severityClassName}>
              {t("common.useVulnerabilityNotifications.severity")}{" "}
              {payload.severity}
              {cvssScore}
            </p>
            {componentLine}
          </div>
        ),
        variant: getToastVariant(isDestructive),
        duration: getToastDuration(isDestructive),
      });
    };

    // Subscribe to vulnerability:created event
    const unsubscribe = on("vulnerability:created", handleVulnerabilityCreated);
    console.log("✅ Subscribed to vulnerability:created event");

    // Cleanup on unmount
    return () => {
      console.log("🧹 Cleaning up vulnerability:created listener");
      unsubscribe();
      off("vulnerability:created", handleVulnerabilityCreated);
    };
  }, [on, off, toast, queryClient, isConnected, t]);
}
